import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  MessageCircle, 
  Shield, 
  Brain, 
  CheckCircle, 
  Loader, 
  ArrowRight,
  Lock,
  FileText
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function AccountSetupProcess({ 
  userEmail, 
  userPassword, 
  authToken: existingAuthToken, // New prop for pre-authenticated users 
  anonymousMessages = [], 
  onComplete, 
  onError 
}) {
  const navigate = useNavigate();
  const setupStartedRef = useRef(false);
  
  // DEBUG: Log component mount
  console.log('🎯 AccountSetupProcess MOUNTED with props:', {
    userEmail,
    hasAuthToken: !!existingAuthToken,
    messageCount: anonymousMessages?.length || 0,
    timestamp: new Date().toISOString(),
    setupStarted: setupStartedRef.current
  });
  
  const [currentStep, setCurrentStep] = useState(0);
  const [stepStatus, setStepStatus] = useState({
    login: 'pending',           // pending, processing, completed, error
    messageConversion: 'pending'
  });
  const [authToken, setAuthToken] = useState(existingAuthToken || null);
  const [stepDetails, setStepDetails] = useState({
    convertedCount: 0
  });
  const [error, setError] = useState('');
  const [allStepsCompleted, setAllStepsCompleted] = useState(false);

  const steps = [
    {
      id: 'login',
      title: 'Loggar in dig automatiskt',
      description: 'Skapar säker session med ditt nya konto',
      icon: Shield,
      color: 'blue'
    },
    {
      id: 'messageConversion',
      title: 'Konverterar dina meddelanden',
      description: `Krypterar och säkrar dina ${anonymousMessages.length} chattmeddelanden`,
      icon: MessageCircle,
      color: 'green'
    }
  ];

  useEffect(() => {
    if (setupStartedRef.current) {
      console.log('⚠️ Setup already started, skipping duplicate run');
      return;
    }
    
    if ((userEmail && userPassword) || existingAuthToken) {
      setupStartedRef.current = true;
      startSetupProcess();
    }
  }, [userEmail, userPassword]); // Remove existingAuthToken from dependencies to prevent re-runs

  const startSetupProcess = async () => {
    try {
      // Step 1: Auto-login (skip if already have token)
      if (existingAuthToken) {
        console.log('✅ Using existing auth token, skipping login');
        setAuthToken(existingAuthToken);
        setStepStatus(prev => ({ ...prev, login: 'completed' }));
      } else {
        await performLogin();
      }
      
      // Step 2: Convert messages
      await convertMessages();
      
      // Step 2.5: Convert life theme answers (if any)
      await convertLifeThemeAnswers();
      
      // All done! - Wait for user to click continue button
      console.log('✅ Setup process complete - waiting for user to continue');
      setAllStepsCompleted(true);
      
    } catch (error) {
      console.error('Setup process failed:', error);
      setError('Något gick fel under kontouppsättningen. Försök igen eller kontakta support.');
      onError(error);
    }
  };

  const performLogin = async () => {
    setCurrentStep(0);
    setStepStatus(prev => ({ ...prev, login: 'processing' }));
    
    try {
      console.log('🔄 Starting auto-login process...');
      
      const loginPayload = new URLSearchParams({
        username: userEmail,
        password: userPassword
      });
      
      const loginResponse = await axios.post(`${API_BASE}/auth/token`, loginPayload, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });

      if (loginResponse.data.access_token) {
        console.log('✅ Auto-login successful');
        const token = loginResponse.data.access_token;
        setAuthToken(token);
        localStorage.setItem('therapy_auth_token', token);
        setStepStatus(prev => ({ ...prev, login: 'completed' }));
      } else {
        throw new Error('No access token received from login');
      }
    } catch (error) {
      console.error('❌ Auto-login failed:', error);
      setStepStatus(prev => ({ ...prev, login: 'error' }));
      throw error;
    }
  };

  const convertMessages = async () => {
    setCurrentStep(1);
    setStepStatus(prev => ({ ...prev, messageConversion: 'processing' }));
    
    try {
      // Check if this specific conversion is already running
      const conversionKey = `conversion_${userEmail}_${anonymousMessages.length}`;
      if (sessionStorage.getItem(conversionKey) === 'running') {
        console.log('⚠️ Message conversion already running for this user, skipping duplicate');
        setStepStatus(prev => ({ ...prev, messageConversion: 'completed' }));
        return;
      }
      
      // Check if messages have already been converted
      const alreadyConverted = sessionStorage.getItem('messagesAlreadyConverted') === 'true';
      console.log('🔍 DEBUG: Message conversion check:');
      console.log('   messagesAlreadyConverted flag:', alreadyConverted);
      console.log('   anonymousMessages.length:', anonymousMessages.length);
      console.log('   anonymousMessages:', anonymousMessages);
      
      if (alreadyConverted) {
        console.log('ℹ️ Messages already converted, skipping conversion step');
        setStepStatus(prev => ({ ...prev, messageConversion: 'completed' }));
        return;
      }
      
      if (anonymousMessages.length === 0) {
        console.log('ℹ️ No messages to convert');
        setStepStatus(prev => ({ ...prev, messageConversion: 'completed' }));
        return;
      }
      
      // Mark conversion as running
      sessionStorage.setItem(conversionKey, 'running');
      
      console.log(`🔄 Converting ${anonymousMessages.length} anonymous messages...`);
      
      // Use authToken from state, fallback to localStorage if null
      const tokenToUse = authToken || localStorage.getItem('therapy_auth_token');
      console.log('🔍 DEBUG: Using token for conversion:', tokenToUse ? `${tokenToUse.substring(0, 20)}...` : 'NULL');
      
      if (!tokenToUse) {
        throw new Error('No authentication token available for message conversion');
      }
      
      const convertResponse = await axios.post(`${API_BASE}/auth/convert-anonymous-messages`, 
        { messages: anonymousMessages },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokenToUse}`
          }
        }
      );
      
      const convertedCount = convertResponse.data.converted_count || 0;
      setStepDetails(prev => ({ ...prev, convertedCount }));
      
      if (convertedCount > 0) {
        console.log(`✅ Successfully converted ${convertedCount} messages`);
        // Clear old anonymous messages
        sessionStorage.removeItem('instantChatMessages');
        sessionStorage.removeItem('pendingAnamnesisMessages');
        sessionStorage.setItem('messagesAlreadyConverted', 'true');
      }
      
      // Clear the running flag
      sessionStorage.removeItem(conversionKey);
      setStepStatus(prev => ({ ...prev, messageConversion: 'completed' }));
      
    } catch (error) {
      console.error('❌ Message conversion failed:', error);
      // Clear the running flag on error too
      const conversionKey = `conversion_${userEmail}_${anonymousMessages.length}`;
      sessionStorage.removeItem(conversionKey);
      setStepStatus(prev => ({ ...prev, messageConversion: 'error' }));
      throw error;
    }
  };

  const convertLifeThemeAnswers = async () => {
    try {
      // Check for pending life theme answers
      const pendingAnswers = sessionStorage.getItem('pendingLifeThemeAnswers');
      
      if (!pendingAnswers) {
        console.log('ℹ️ No life theme answers to convert');
        return;
      }
      
      const answers = JSON.parse(pendingAnswers);
      
      if (answers.length === 0) {
        console.log('ℹ️ No life theme answers to convert');
        return;
      }
      
      console.log(`🔄 Converting ${answers.length} life theme answers...`);
      
      // Use authToken from state, fallback to localStorage if null
      const tokenToUse = authToken || localStorage.getItem('therapy_auth_token');
      
      if (!tokenToUse) {
        throw new Error('No authentication token available for life theme conversion');
      }
      
      // Transform answers to match backend schema
      const formattedAnswers = answers.map(a => ({
        question_id: a.question_id,
        answer_value: a.answer_value
      }));
      
      const convertResponse = await axios.post(
        `${API_BASE}/api/life-theme-identifier/convert-anonymous-answers`, 
        formattedAnswers,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokenToUse}`
          }
        }
      );
      
      const convertedCount = convertResponse.data.converted_count || 0;
      
      if (convertedCount > 0) {
        console.log(`✅ Successfully converted ${convertedCount} life theme answers`);
        // Clear old answers after successful conversion
        sessionStorage.removeItem('pendingLifeThemeAnswers');
        sessionStorage.removeItem('selectedLifeTheme');
      }
      
    } catch (error) {
      console.error('⚠️ Life theme answer conversion failed (non-critical):', error);
      // Don't throw - this is non-critical, user can retake assessment later
      console.log('ℹ️ Continuing without life theme conversion - can be completed later');
    }
  };

  const getStepStatusIcon = (stepId, index) => {
    const status = stepStatus[stepId];
    const isActive = currentStep === index;
    
    if (status === 'completed') {
      return <CheckCircle className="w-6 h-6 text-success-600" />;
    } else if (status === 'processing' || isActive) {
      return <Loader className="w-6 h-6 text-brand-primary animate-spin" />;
    } else if (status === 'error') {
      return <div className="w-6 h-6 rounded-full bg-error-500 flex items-center justify-center text-white text-sm">!</div>;
    } else {
      return <div className="w-6 h-6 rounded-full bg-light" />;
    }
  };

  const getStepColor = (stepId, index) => {
    const status = stepStatus[stepId];
    const isActive = currentStep === index;
    
    if (status === 'completed') return 'text-success-700';
    if (status === 'processing' || isActive) return 'text-brand-primary';
    if (status === 'error') return 'text-error-700';
    return 'text-tertiary';
  };

  const isAllCompleted = () => {
    return Object.values(stepStatus).every(status => status === 'completed');
  };

  const handleContinue = () => {
    console.log('👆 User clicked continue button');
    onComplete(authToken, stepDetails);
  };

  return (
    <div className="min-h-screen flex items-center justify-center ai-bg font-sans">
      <div className="bg-white px-10 rounded-xl p-6 mb-6 border border-success-500/10 w-full max-w-2xl px-custom-6 mt-24">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mx-auto mb-4">
            <img src="/images/park.png" alt="Välkommen" className="w-56 h-40 object-contain" />
          </div>
          <h1 className="text-3xl font-bold text-primary mb-2 font-inter">
            Välkommen till din resa mot förändring
          </h1>
          <p className="text-secondary text-lg">
            <strong>Livsteman</strong> är de destruktiva mönster i dina tankar, känslor och beteenden som håller dig tillbaka. 
                    När du väl känner till dem blir det så mycket lättare att förändra ditt liv – för då kan du börja hantera dem medvetet.
          </p>
        </div>

        {/* How It Works */}
        <div className="mb-6 px-10">
          <h3 className="font-semibold text-gray-900 mb-4">Så här fungerar det:</h3>
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <span className="font-bold text-brand-primary text-lg">1.</span>
              <div>
                <p className="text-gray-700"><strong>Upptäck dina destruktiva mönster</strong> – genom korta frågor</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <span className="font-bold text-brand-primary text-lg">2.</span>
              <div>
                <p className="text-gray-700"><strong>Förstå hur de fungerar</strong> – AI hjälper dig utforska djupare</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <span className="font-bold text-brand-primary text-lg">3.</span>
              <div>
                <p className="text-gray-700"><strong>Lär dig hantera dem bättre</strong> – förändra steg för steg</p>
              </div>
            </div>
          </div>
        </div>

        {isAllCompleted() && (
          <div className="text-center mt-6">
            <button
              onClick={handleContinue}
              className="bg-brand-primary text-white px-8 py-4 rounded-xl font-semibold text-lg hover:from-brand-primary-dark hover:to-brand-secondary/90 transition-custom duration-200 flex items-center space-x-2 mx-auto shadow-custom-sm hover:shadow-custom-md"
            >
              <span>Fortsätt</span>
              <ArrowRight className="w-5 h-5" />
            </button>
            
          </div>
        )}

        {/* Security Information */}
          <div className="flex items-start space-x-3 px-10 mt-10">
              <div>
                <h3 className="font-semibold text-primary mb-2">Banksäker sekretess</h3>
                <div className="text-sm text-secondary space-y-1">
                  <p>• <strong>Användare-specifik kryptering:</strong> Endast du kan se dina livstema-svar</p>
                  <p>• <strong>End-to-end säkerhet:</strong> Samtalen med AI krypteras från din enhet</p>
                  <p>• <strong>GDPR-kompatibel:</strong> Du äger din data och kan radera den när som helst</p>
                  <p>• <strong>Zero-knowledge:</strong> Inte ens vi kan läsa dina privata svar</p>
                </div>
              </div>
            </div>

        
      </div>
    </div>
  );
}

export default AccountSetupProcess;